import { computed, signal } from '@preact/signals-react'
import type { Mode } from '../../mode/mode/mode.types'
import type { ControllerMates } from '../controllerMates/controllerMates.types'
import type { LocalMates, LocalMate } from './localMates.types'
import { getRandomId } from '@merels/game-logic'
import type { OmniMates } from '../omniMates/omniMates.types'
import marvinThumbnail from '../../../assets/marvin.png'

import { getOrGenerateClientId } from '../../utils/clientId'

export interface LocalMatesDeps {
  mode: Mode
  controllerMates: ControllerMates
  omniMates: OmniMates
}

const PERSISTENT_GUEST_ID_KEY = 'merels_persistentMateId'
const LOCAL_MATE_NAME_KEY_PREFIX = 'merels_localMateName_'

function getSingleMateId(): string {
  if (typeof window === 'undefined') return 'server'
  try {
    const key = 'merels_singleMateId'
    const existing = window.sessionStorage.getItem(key)
    if (existing) return existing
    const created = getRandomId(8)
    window.sessionStorage.setItem(key, created)
    return created
  } catch {
    return getRandomId(8)
  }
}

function getMateIdForController(controllerId: string): string {
  if (typeof window === 'undefined') return 'server'
  try {
    const key = `merels_controllerMateId_${controllerId}`
    const existing = window.sessionStorage.getItem(key)
    if (existing) return existing
    const created = getRandomId(8)
    window.sessionStorage.setItem(key, created)
    return created
  } catch {
    return getRandomId(8)
  }
}

/**
 * Opaque, stable per-mate-slot identity that outlives a single session (unlike
 * mateId, which is sessionStorage-scoped). Used only as a lookup key so a room
 * can remember this guest's uploaded thumbnail across visits - never holds the
 * thumbnail data itself.
 */
function getPersistentGuestId(): string {
  if (typeof window === 'undefined') return 'server'
  try {
    const existing = window.localStorage.getItem(PERSISTENT_GUEST_ID_KEY)
    if (existing) return existing
    const created = getRandomId(8)
    window.localStorage.setItem(PERSISTENT_GUEST_ID_KEY, created)
    return created
  } catch {
    return getRandomId(8)
  }
}

function getLocalMateNameKey(persistentId: string): string {
  return `${LOCAL_MATE_NAME_KEY_PREFIX}${persistentId}`
}

function readStoredLocalMateName(persistentId: string): string {
  if (typeof window === 'undefined') return ''
  try {
    return window.localStorage.getItem(getLocalMateNameKey(persistentId)) || ''
  } catch {
    return ''
  }
}

/**
 * Current implementation:
 * - shared-screen local: local mates come from controllers (`controllerMates`)
 * - otherwise: local mates are currently unknown/unsupported here (empty)
 *
 * This is intentionally minimal; it gives us a single place to evolve local mate sources
 * (regular/Omni/bot) without tying the rest of the system to controllers.
 */
export function makeLocalMatesCore(deps: LocalMatesDeps): LocalMates {
  const { mode, controllerMates, omniMates } = deps
  const clientId = getOrGenerateClientId()
  const persistentGuestId = getPersistentGuestId()

  const localMateName = signal(readStoredLocalMateName(persistentGuestId))
  const authenticatedMateName = signal('')
  const localMateThumbnail = signal<string | undefined>(undefined)
  const localBots = signal<LocalMate[]>([])
  const omniMatesList = signal<Array<{ mateId: string; name: string }>>(
    omniMates.getDefaultOmniMates()
  )

  function makeRealMate(): LocalMate {
    return {
      clientId,
      mateId: getSingleMateId(),
      persistentId: persistentGuestId,
      // The internal `test:` URL harness enters Omni mode without showing name entry.
      // Its real mate remains an observer beside the synthetic mates, but still needs a
      // valid name so room roster validation does not reject Alice and Bob with it.
      name: localMateName.value || authenticatedMateName.value || (mode.isOmniMode() ? 'Observer' : ''),
      type: 'single',
      thumbnailUrl: localMateThumbnail.value,
    }
  }

  const localMates = computed<LocalMate[]>(() => {
    const isShared = Boolean(mode.computedMode.value.sharedScreen)
    let baseMates: LocalMate[] = []

    if (isShared) {
      baseMates = controllerMates.mates.value.map((m) => ({
        clientId,
        mateId: getMateIdForController(m.id),
        persistentId: m.profileId,
        controllerId: m.id,
        name: m.name,
        type: mode.isOmniMode() ? 'omni' : 'shared',
        isOmniMate: mode.isOmniMode() ? true : undefined,
        registeredAt: m.registeredAt,
        thumbnailUrl: m.thumbnailUrl,
      }))
    } else if (mode.isOmniMode()) {
      // Omni mode ADDS its fake mates alongside this client's own real mate rather
      // than replacing it - the two identities stay distinct (see isOmniMate's doc
      // comment in @merels/room/types): the real mate keeps its own name/video and
      // is never eligible for a player seat, while the Omni mates are what actually
      // fill seats (see debugPanelVM.ts's openGameSeatingTheseMates) and never carry
      // video (see video.ts's role, and mateListVM.ts's getVideoFields).
      baseMates = [
        makeRealMate(),
        ...omniMatesList.value.map((m) => ({
          clientId,
          mateId: m.mateId,
          name: m.name,
          type: 'omni' as const,
          isOmniMate: true,
        })),
      ]
    } else {
      baseMates = [makeRealMate()]
    }

    return [...baseMates, ...localBots.value]
  })

  const localMateIds = computed(() => new Set(localMates.value.map((m) => m.mateId)))

  const tempName = signal('')
  const showNameDialog = signal(false)

  function start(): void {
    controllerMates.start()
  }

  function stop(): void {
    controllerMates.stop()
  }

  function setAuthenticatedName(name: string): void {
    authenticatedMateName.value = name.trim()
  }

  function updateMateName(mateId: string, name: string): void {
    // Shared-screen: mateId maps to controllerId via sessionStorage key.
    const isShared = Boolean(mode.computedMode.value.sharedScreen)
    if (isShared) {
      for (const m of localMates.value) {
        if (m.mateId !== mateId) continue
        if (!m.controllerId) return
        controllerMates.updateMateName(m.controllerId, name)
        return
      }
    } else {
      localMateName.value = name
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(getLocalMateNameKey(persistentGuestId), name)
      }
    }
  }

  function updateMateThumbnail(mateId: string, thumbnailUrl: string): void {
    // Shared-screen: mateId maps to controllerId via sessionStorage key.
    const isShared = Boolean(mode.computedMode.value.sharedScreen)
    if (isShared) {
      for (const m of localMates.value) {
        if (m.mateId !== mateId) continue
        if (!m.controllerId) return
        controllerMates.updateMateThumbnail(m.controllerId, thumbnailUrl)
        return
      }
    } else {
      // Single mode: update local mate thumbnail (session-only, not persisted client-side).
      localMateThumbnail.value = thumbnailUrl
    }
  }

  function saveTempName(): void {
    const name = tempName.value
    if (!name) return

    const currentMates = localMates.value
    if (currentMates.length > 0) {
      updateMateName(currentMates[0].mateId, name)
    }
    showNameDialog.value = false
  }

  function addAIPlayer(_name?: string, botType?: string, botConfig?: Record<string, any>): void {
    if (localBots.value.length >= 1) return

    const actualBotType = botType || 'mcts'
    const botId = `bot_${getRandomId(8)}`
    const newBot: LocalMate = {
      clientId,
      mateId: botId,
      persistentId: botId,
      name: 'Marvin',
      thumbnailUrl: marvinThumbnail,
      isAI: true,
      botType: actualBotType as any,
      botConfig,
      type: 'single',
    }
    localBots.value = [...localBots.value, newBot]
  }

  function setOmniMateCount(count: number): void {
    if (!mode.isOmniMode()) return
    const clamped = Math.max(0, Math.min(Math.trunc(count), omniMates.maxOmniMates))
    let list = omniMatesList.value
    if (clamped === list.length) return
    while (list.length < clamped) {
      const names = list.map((m) => m.name)
      list = [...list, omniMates.createOmniMate(names, list.length)]
    }
    if (list.length > clamped) {
      list = list.slice(0, clamped)
    }
    omniMatesList.value = list
  }

  function removeMate(mateId: string): void {
    if (mateId.startsWith('bot_')) {
      localBots.value = localBots.value.filter((b) => b.mateId !== mateId)
    }
  }

  return {
    localMates: localMates as any,
    localMateIds,
    tempName,
    showNameDialog,
    start,
    stop,
    setAuthenticatedName,
    updateMateName,
    updateMateThumbnail,
    saveTempName,
    addAIPlayer,
    setOmniMateCount,
    removeMate,
  }
}
